import { styled } from '@mui/material/styles';
import { ForwardedRef, ReactNode, CSSProperties, ReactElement, ElementType } from 'react';
import React from 'react';
import { text } from '../../themes/text';

interface Props {
  variant: keyof typeof variants;
  color: string;
  children: ReactNode | string;
  as?: ElementType<any> & keyof HTMLElementTagNameMap;
  className?: string;
  testId?: string;
  style?: CSSProperties;
  onClick?: () => void;
}

const { size, lineHeight, weight, letterSpace, font } = text;

export const variants = {
  h1: {
    fontFamily: font.salvaTest,
    fontSize: size.extraLarge,
    lineHeight: lineHeight.extraLarge,
    fontWeight: weight.regular,
    letterSpacing: letterSpace.regular,
  },
  h2: {
    fontFamily: font.basierMedium,
    fontSize: size.large,
    lineHeight: lineHeight.large,
    fontWeight: weight.medium,
    letterSpacing: letterSpace.regular,
  },
  h3: {
    fontFamily: font.argestaRegular,
    fontSize: size.mediumLarge,
    lineHeight: lineHeight.mediumLarge,
    fontWeight: weight.regular,
    letterSpacing: letterSpace.regular,
  },
  h4: {
    fontFamily: font.basierMedium,
    fontSize: size.medium,
    lineHeight: lineHeight.medium,
    fontWeight: weight.medium,
    letterSpacing: letterSpace.regular,
  },
  h5: {
    fontFamily: font.basierRegular,
    fontSize: size.medium,
    lineHeight: lineHeight.medium,
    fontWeight: weight.regular,
    letterSpacing: letterSpace.regular,
  },
  subtitle1: {
    fontFamily: font.basierMedium,
    fontSize: size.mediumSmall,
    lineHeight: lineHeight.mediumSmall,
    fontWeight: weight.medium,
    letterSpacing: letterSpace.regular,
  },
  subtitle2: {
    fontFamily: font.basierMedium,
    fontSize: size.mediumSmall,
    lineHeight: lineHeight.mediumSmall,
    fontWeight: weight.medium,
    letterSpacing: letterSpace.large,
  },
  body: {
    fontFamily: font.basierRegular,
    fontSize: size.mediumSmall,
    lineHeight: lineHeight.mediumSmall,
    fontWeight: weight.medium,
    letterSpacing: letterSpace.regular,
  },
  caption: {
    fontFamily: font.basierRegular,
    fontSize: size.small,
    lineHeight: lineHeight.extraSmall,
    fontWeight: weight.regular,
    letterSpacing: letterSpace.regular,
  },
  srOnly: {
    fontFamily: font.basierMedium,
    fontSize: size.small,
    lineHeight: lineHeight.extraSmall,
    fontWeight: weight.medium,
    letterSpacing: letterSpace.regular,
  },
  overline: {
    fontFamily: font.basierRegular,
    fontSize: size.extraSmall,
    lineHeight: lineHeight.extraSmall,
    fontWeight: weight.regular,
    letterSpacing: letterSpace.regular,
  },
};

const StyledTypography = styled('p')<Props>`
  color: ${({ color }) => color};
  ${({ variant }) => variants[variant]}
`;
interface PropsWithRef extends Props {
  ref: ForwardedRef<HTMLParagraphElement | null>;
}
const StyledTypographyWithRef = styled(StyledTypography)<PropsWithRef>``;

// eslint-disable-next-line react/display-name
export const TypographyWithRef = React.forwardRef<HTMLParagraphElement | null, Props>(
  (props, ref) => {
    return (
      <StyledTypographyWithRef as='div' variant={props.variant} color={props.color} ref={ref}>
        {props.children}
      </StyledTypographyWithRef>
    );
  }
);

export function Typography({
  variant,
  color,
  children,
  as,
  testId = '',
  style,
  onClick,
}: Props): ReactElement {
  return (
    <StyledTypography
      onClick={onClick}
      variant={variant}
      color={color}
      as={as}
      data-testid={testId}
      style={style}
    >
      {children}
    </StyledTypography>
  );
}
